import { applyFinalScoresToSummary } from "./dpatManualScoreHelpers";

const TOTAL_MAIN_CI_COUNT = 4;

export const QA_MEMO_TEMPLATE_VERSION = 1;
export const QA_MEMO_TABLE_ID = "DPAT_MEMO";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function highlightHtml(value) {
  const text = value == null || value === "" ? "—" : String(value);
  return `<span class="dpat-qa-memo-highlight">${escapeHtml(text)}</span>`;
}

function countFulfilled(items) {
  return (items || []).filter((item) => item?.fulfilled).length;
}

function getMainComplianceItems(complianceIndicators) {
  return (complianceIndicators || []).filter(
    (item) => item?.no && !String(item.no).toLowerCase().includes("sub-total")
  );
}

function formatCiSummary(complianceIndicators) {
  const mainItems = getMainComplianceItems(complianceIndicators);

  if (mainItems.length > 0) {
    return mainItems
      .map((item) => `${item.no}: ${item.fulfilled ? "Fulfilled" : "Not Fulfilled"}`)
      .join("; ");
  }

  const fulfilled = countFulfilled(complianceIndicators);
  if (fulfilled === 0 && !complianceIndicators?.length) {
    return "—";
  }

  const notFulfilled = Math.max(0, TOTAL_MAIN_CI_COUNT - fulfilled);
  return `${fulfilled} Fulfilled, ${notFulfilled} Not Fulfilled`;
}

function formatScorePair(actual, max) {
  const actualNum = Number(actual);
  const maxNum = Number(max);
  if (!Number.isFinite(actualNum) || !Number.isFinite(maxNum) || maxNum <= 0) {
    return "—";
  }
  const pct = ((actualNum / maxNum) * 100).toFixed(2);
  return `${actualNum} / ${maxNum} (${pct}%)`;
}

export function resolveDistrictDisplayName(district) {
  if (!district) return "the District Assembly";
  if (typeof district === "string") return district;
  return district.label || district.name || district.title || "the District Assembly";
}

export function getDpatQaMemoContent({ districtName, year, summary }) {
  const assessmentYear = year || new Date().getFullYear();
  const name = districtName || "the District Assembly";
  const alreadyResolved = Boolean(
    summary?.totals &&
      (summary.ciSubIndicators || summary.sdiSubIndicators || summary.complianceIndicators),
  );
  const resolved = alreadyResolved ? summary : applyFinalScoresToSummary(summary) || {};
  const totals = resolved.totals || {};
  const complianceIndicators = resolved.complianceIndicators || [];
  const ciItems = getMainComplianceItems(complianceIndicators).map((item) => ({
    no: item.no,
    status: item.fulfilled ? "Fulfilled" : "Not Fulfilled",
  }));

  return {
    assessmentYear,
    districtName: name,
    overallScore: formatScorePair(totals.grandTotalActualScore, totals.grandTotalMaxScore),
    sdiScore: formatScorePair(totals.sdiTotalActualScore, totals.sdiTotalMaxScore),
    piScore: formatScorePair(totals.piTotalActualScore, totals.piTotalMaxScore),
    ciItems,
    ciSummary: formatCiSummary(complianceIndicators),
  };
}

export function buildDpatQaMemoTemplate({ districtName, year, summary }) {
  const content = getDpatQaMemoContent({ districtName, year, summary });
  const ciLines = content.ciItems.length
    ? content.ciItems
        .map((item) => `<p class="dpat-qa-memo-line">${highlightHtml(`${item.no}: ${item.status}`)}</p>`)
        .join("")
    : `<p class="dpat-qa-memo-line">Compliance Indicators: ${highlightHtml(content.ciSummary)}</p>`;

  return `<div class="dpat-qa-memo-template" data-qa-memo-template="true"><div class="dpat-qa-memo-document-root"><h2 class="dpat-qa-memo-title"><u>DPAT X PROVISIONAL RESULTS (MEMO)</u></h2><p class="dpat-qa-memo-line">This Memo provides a summary of the provisional results for ${highlightHtml(content.districtName)} on its performance after the conduct of DPAT X (${escapeHtml(content.assessmentYear)}) assessment.</p><p class="dpat-qa-memo-line">The Quality Assurance Review (QAR) Team after triangulating DDDP generated scores, Technical Team scores and other relevant documents, ${highlightHtml(content.districtName)} scored ${highlightHtml(content.overallScore)}.</p><p class="dpat-qa-memo-line">Details of the results as per the DPAT Components are as follows:</p>${content.ciItems.length ? `<p class="dpat-qa-memo-line">Compliance Indicators:</p>${ciLines}` : ciLines}<p class="dpat-qa-memo-line">Service Delivery Indicators: ${highlightHtml(content.sdiScore)}</p><p class="dpat-qa-memo-line">Performance Indicators: ${highlightHtml(content.piScore)}</p><p class="dpat-qa-memo-line">Bonus(es) awarded are part of the total score as indicated below.</p></div></div>`;
}

export function mergeQaMemoDocument(templateHtml, addendumHtml) {
  const addendum = (addendumHtml || "").trim();
  if (!addendum) {
    return templateHtml || "";
  }
  return `${templateHtml || ""}<div data-qa-memo-addendum="true">${addendum}</div>`;
}

export function parseQaMemoAddendum(memo) {
  const stored = memo?.dddpData?.qaMemoAddendum;
  if (stored != null) {
    return String(stored);
  }

  const comments = memo?.comments || "";
  const match = comments.match(
    /<div[^>]*data-qa-memo-addendum=["']true["'][^>]*>([\s\S]*)<\/div>\s*$/i
  );
  return match ? match[1] : "";
}
